import type { ShortcutKeysInput } from '@/components/util/shortcuts'
import type { ComponentProps } from 'react'

import { tv } from 'tailwind-variants'

import { useCellDerivedState } from '../CellContext'
import { gridStateActions } from '../../grid/grid.store'

import { Menu } from '@ark-ui/react/menu'
import { Portal } from '@ark-ui/react/portal'
import { Tooltip } from '@ark-ui/react/tooltip'

import { KbdGrouper } from '@/components/ui/kbd'
import { Toolbar } from '@/components/ui/Toolbar'

const cellToolbarStyle = tv({
  base: [
    'absolute top-0 -translate-y-full',
    '-mt-2', // Gap to cell
    'flex items-center gap-0.5 p-1',
    'bg-neutral-800 shadow-lg border border-white/10',
    'shadow-2xl rounded-md',
    'text-neutral-400 text-sm',
    '[&_svg]:size-4', // Set size for all item icons
    'pointer-events-auto',
  ],
})

/**
 * Toolbar that opens above Cells, it is sticky and accessible.
 */
export function CellToolbar({
  children,
  onKeyTab,
}: Pick<ComponentProps<typeof Toolbar>, 'children' | 'onKeyTab'>) {
  const { isSelected, isEditing } = useCellDerivedState()

  if (!isSelected && !isEditing) return null

  return (
    // {/* Virtual track for toolbar */}
    <div
      role="none"
      className="absolute inset-x-0 top-0 bottom-6 select-none pointer-events-none"
    >
      {/* Sticky toolbar anchor */}
      <div
        role="none"
        className="sticky z-20 top-(--headers-size) pointer-coarse:top-(--headers-size) left-0 right-0 h-0 flex justify-center"
      >
        <Toolbar
          aria-label="Cell toolbar"
          className={cellToolbarStyle()}
          onKeyEscape={
            isEditing
              ? gridStateActions.editor.discard
              : gridStateActions.resetSelectedCell
          }
          onKeyTab={onKeyTab ? onKeyTab : undefined}
        >
          {children}
        </Toolbar>
      </div>
    </div>
  )
}

const cellToolbarItemsStyles = tv({
  base: [
    'rounded',
    'flex items-center',
    'border border-transparent ',
    'select-none',
    'focus-visible:outline-none focus-visible:ring-offset-3 focus-visible:ring-offset-neutral-800',
  ],
  defaultVariants: {
    element: 'button',
  },
  variants: {
    element: {
      button: [
        'p-1 justify-center gap-1',
        // Default State
        'text-neutral-400 hover:text-neutral-200 hover:bg-white/5 cursor-pointer',
        // Active State
        'data-[active=true]:text-blue-400 data-[active=true]:bg-blue-500/15 data-[active=true]:border-blue-500/30 data-[active=true]:cursor-pointer',
        // Disabled State
        'data-[disabled=true]:text-neutral-600 data-[disabled=true]:bg-transparent data-[disabled=true]:opacity-60 data-[disabled=true]:cursor-not-allowed!',
      ],

      menuItem: [
        'group w-full px-2.5 py-1.5 justify-between gap-3 text-sm text-left',
        // Default State
        'text-neutral-400 bg-transparent',
        // Hover & Keyboard Highlight State
        'data-highlighted:text-neutral-200 data-highlighted:bg-white/5 data-highlighted:cursor-pointer',
        // Active / Checked State (Radio & Checkbox Items)
        'data-[state=checked]:text-blue-400 data-[state=checked]:bg-blue-500/15 data-[state=checked]:border-blue-500/30 data-[state=checked]:font-medium',
        // Disabled State
        'data-disabled:text-neutral-600 data-disabled:bg-transparent data-disabled:opacity-60 data-disabled:cursor-not-allowed!',
      ],
    },
  },
})

export function CellToolbarItem({
  tooltip,
  tooltipShortcut,
  active,
  disabled,
  className,
  children,
  ...props
}: ComponentProps<'button'> & {
  active?: boolean
  tooltip: string
  tooltipShortcut?: ShortcutKeysInput
}) {
  return (
    <CellToolbarItemTooltip content={tooltip} shortcut={tooltipShortcut}>
      {/* ArkUI requires a wrapper element */}
      <span role="none">
        <button
          className={cellToolbarItemsStyles({ element: 'button', className })}
          data-active={active}
          aria-pressed={active}
          // Map disabled prop to data (styling) and ARIA (a11y), but don't set native disabled prop, it disabled focus.
          aria-disabled={disabled}
          data-disabled={disabled}
          // Reuse tooltip for ARIA
          aria-label={tooltip}
          {...props}
        >
          {children}
        </button>
      </span>
    </CellToolbarItemTooltip>
  )
}

function CellToolbarItemTooltip({
  content,
  shortcut,
  children,
  ...props
}: ComponentProps<typeof Tooltip.Root> & {
  content: React.ReactNode
  shortcut?: ShortcutKeysInput
}) {
  return (
    <Tooltip.Root
      closeDelay={100}
      openDelay={400}
      closeOnScroll={false}
      closeOnEscape={false}
      positioning={{
        placement: 'top',
        strategy: 'fixed',
        offset: { mainAxis: 8 },
      }}
      {...props}
    >
      <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>

      {/* Portal to body to secape cell 'isolate' bounds */}
      <Portal>
        <Tooltip.Positioner className="z-100!">
          <Tooltip.Content className="bg-gray-800 border border-neutral-700 text-sm text-neutral-200 px-2 py-1 rounded-md shadow-xl pointer-events-none whitespace-nowrap flex items-center gap-1.5">
            {content}

            {/* Shortcuts */}
            {shortcut && <KbdGrouper keys={shortcut} />}

            <Tooltip.Arrow className="[--arrow-size:8px] [--arrow-background:var(--color-gray-800)]">
              <Tooltip.ArrowTip className="rounded-xs " />
            </Tooltip.Arrow>
          </Tooltip.Content>
        </Tooltip.Positioner>
      </Portal>
    </Tooltip.Root>
  )
}

export function CellToolbarMenu({
  id,
  trigger,
  children,
  ...props
}: ComponentProps<typeof Menu.Root> & {
  trigger: React.ReactNode
  children?: React.ReactNode
}) {
  return (
    <Menu.Root
      id={id}
      positioning={{
        placement: 'bottom-start',
      }}
      {...props}
    >
      <Menu.Trigger asChild>{trigger}</Menu.Trigger>

      <Portal>
        <Menu.Positioner>
          <Menu.Content className="z-50 min-w-32 bg-neutral-900 border border-neutral-800 p-1.5 rounded-md shadow-xl shadow-black/60 focus:outline-none">
            {children}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  )
}

export function CellToolbarMenuItem({
  type = 'item',
  children,
  className,
  ...props
}: ComponentProps<typeof Menu.Item> & {
  type?: 'item' | 'radio' | 'checkbox'
}) {
  const styles = cellToolbarItemsStyles({ element: 'menuItem', className })

  if (type === 'radio') {
    return (
      <Menu.RadioItem className={styles} {...props}>
        {children}
      </Menu.RadioItem>
    )
  }
  return (
    <Menu.Item className={styles} {...props}>
      {children}
    </Menu.Item>
  )
}
